import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormSetError } from "react-hook-form";
import * as z from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { zodKeys } from "@/lib/utils";
import { ResearchFolder, researchFolderApi } from "@/api/research";
import { Checkbox } from "@/components/ui/checkbox";
import { useAdminPermission } from "@/hooks/usePermission";
import { useTranslation } from "react-i18next";
import { useOptimisticLocking } from "@/hooks/useOptimisticLocking";
import { ConflictResolutionDialog } from "@/components/ui/conflict-resolution-dialog";
import {
  withVersion,
  createOptimisticLockingToasts,
} from "@/lib/optimisticLockingHelpers";
import { researchFolderFieldConfig } from "@/lib/fieldDiffUtils";

export function AddUpdateResearchFolder({
  folder,
  trigger,
  refresh,
}: {
  folder?: ResearchFolder;
  trigger: JSX.Element;
  refresh?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;

  const { loading, user } = contextValues;
  const { setLoading } = set;

  const hasAdminPermission = useAdminPermission();
  const toasts = createOptimisticLockingToasts(
    t("research.title.research_folder"),
    t,
  );

  const FormSchema = z.object({
    name: z.string().min(3, {
      message: t("common.statictext.name_too_short"),
    }),
    public: z.boolean().default(false).optional(),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      public: false,
    },
  });

  // Function to get current form data for conflict comparison
  const getCurrentFormData = useCallback(
    (version: number): ResearchFolder => {
      const formData = form.getValues();
      return {
        id: folder?.id,
        version: version,
        name: formData.name,
        public: formData.public || false,
      } as ResearchFolder;
    },
    [form, folder?.id],
  );

  // Optimistic locking hook
  const {
    currentVersion,
    updateVersion,
    updateWithLocking,
    conflictState,
    handleRefresh,
    handleForceOverwrite,
    clearConflict,
  } = useOptimisticLocking<ResearchFolder>({
    initialVersion: folder?.version || 0,
    resourceId: folder?.id || 0,
    resourceName: t("research.title.research_folder"),
    apiRead: (id) =>
      researchFolderApi.read({
        id: id.toString(),
        token: user?.access,
      }),
    apiUpdate: (id, data) =>
      researchFolderApi.update({
        id: id.toString() + "/",
        data,
        token: user?.access,
      }),
    onUpdateSuccess: () => {
      toast(toasts.updateSuccess);
    },
    onConflict: () => {
      toast(toasts.conflictDetected);
    },
    onUpdateError: (error) => {
      toast(toasts.error(error));
    },
    diffConfig: researchFolderFieldConfig,
    localData: folder ? getCurrentFormData(folder.version) : undefined,
  });

  const errorHandler = useApiErrorHandler("AddUpdateResearchFolder", {
    setFormError: form.setError as UseFormSetError<Record<string, unknown>>,
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      setLoading?.(true);
      if (folder && folder.id) {
        // UPDATE with optimistic locking
        const result = await updateWithLocking(
          withVersion(
            {
              name: data.name,
              public: data.public,
              updated_by: user?.user.id,
            },
            currentVersion,
          ),
        );

        if (result.conflict || !result.success) return;
      } else {
        await researchFolderApi.create({
          url: "/",
          data: {
            name: data.name,
            public: data.public,
            created_by: user?.user.id,
          },
          token: user?.access,
        });
        toast({
          title: t("common.toast.added_successfully", {
            resource: t("research.title.research_folder"),
          }),
        });
        form.reset();
      }
      await refresh?.();
      setOpen(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    if (folder) {
      updateVersion(folder.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          folder[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  const buttonAction = folder?.id
    ? t("common.button.update")
    : t("common.button.add");

  // Conflict resolution handlers
  const handleRefreshFolder = async () => {
    if (!folder?.id) return;
    const refreshed = await handleRefresh();
    if (refreshed) {
      // Update form with refreshed data
      updateVersion(refreshed.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          refreshed[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      toast(toasts.refreshSuccess);
    }
  };

  const handleForceOverwriteFolder = async () => {
    if (!conflictState.latestVersion || !folder?.id) return;
    const formData = form.getValues();
    const result = await handleForceOverwrite(
      withVersion(
        {
          name: formData.name,
          public: formData.public,
          updated_by: user?.user.id,
        },
        conflictState.latestVersion,
      ),
    );
    if (result.success) {
      toast(toasts.overwriteSuccess);
      await refresh?.();
      setOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          className="w-full md:w-[500px] max-h-screen overflow-y-auto"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full flex flex-col gap-8"
            >
              <DialogHeader>
                <DialogTitle>
                  {buttonAction} {t("research.title.research_folder")}
                </DialogTitle>
              </DialogHeader>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("research.label.folder_name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.folder_name")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {hasAdminPermission && (
                <FormField
                  control={form.control}
                  name="public"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="ml-2">
                        {t("research.label.accessible_by_everyone")}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              )}
              <DialogFooter>
                <Button disabled={loading} type="submit">
                  {loading ? t("common.button.loading") : buttonAction}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={conflictState.isOpen}
        onOpenChange={(open) => !open && clearConflict()}
        currentVersion={currentVersion}
        latestVersion={conflictState.latestVersion}
        resourceName={t("research.title.research_folder")}
        onRefresh={handleRefreshFolder}
        onOverwrite={handleForceOverwriteFolder}
        loading={loading}
        fieldDiffs={conflictState.fieldDiffs}
        fieldLabels={Object.fromEntries(
          Object.entries(researchFolderFieldConfig.fields).map(
            ([field, config]) => [field, t(config.label)],
          ),
        )}
      />
    </>
  );
}
